import { useState } from 'react';
import {
  AgencyTypeIcon,
  Badge,
  BUTTON_CLASS,
  DataTable,
  formatUtcDate,
  MetaRow,
  neutralButtonStyle,
  StatusDot,
  token,
  type BadgeTone,
  type DataTableColumn,
} from '@bp-files/ui';
import { SPACE_LIST_COPY } from '@/components/SpaceList/spaceList.copy';
import {
  RowActionsConfirm,
  RowActionsItem,
  RowActionsMenu,
} from '@/components/SpaceList/RowActionsMenu';
import { AddAgencyPanel } from '@/components/SpaceList/AddAgencyPanel';
import { CreateSpacePanel } from '@/components/SpaceList/CreateSpacePanel';
import { RenameSpacePanel } from '@/components/SpaceList/RenameSpacePanel';
import { ExpiryWindowPanel } from '@/components/SpaceList/ExpiryWindowPanel';
import { ACTIVE_WINDOW_DEFAULT_DAYS } from '@/lib/expiry';
import { MembersPanel } from '@/components/SpaceList/MembersPanel';
import { useSpaces } from '@/lib/useSpaces';
import { acceptMembership, deleteSpace, type SpaceSummary } from '@/lib/api';

/**
 * The space table — §4's surface, and **it is a table of rooms now, not of
 * counterpart agencies.**
 *
 * ADR-0032 moved it off Files, on the ground that these are acts a member cannot
 * perform and Files is where members work. ADR-0071 changed what a row *is*: the
 * list was of counterparts, because that was the only handle a link offered, and
 * it is now of spaces — each with a name somebody chose (CON-22), a roster
 * (CON-11 as amended), and an agency appearing in as many rows as it has
 * memberships.
 *
 * **The acts that used to live on a row live on the roster.** CON-14's two verbs
 * share one route and are told apart by their target (ADR-0071), so the control
 * has to sit beside the agency it names — which is a row of `MembersPanel`, not
 * a row of this table. What is left here is CON-6's accept (this agency's own,
 * and the one act a row can express without naming anybody), the way into the
 * roster, and FIL-9's copyable address.
 *
 * **`ConnectPanel` is gone and two panels replaced it.** It made two calls
 * behind one button and had to name the room before the API had resolved the
 * invited domain — see `CreateSpacePanel`'s header for the defect that killed,
 * and for what went with it (`namesByDomain`, and the half-done error copy that
 * named an empty room).
 *
 * Admins get every control below. **That is visibility, not authorization** —
 * SEC-5 puts authorization in the API, which refuses on every request from the
 * token-resolved identity regardless of what this component rendered. The
 * `isAdmin` prop stays for that reason even though `AdminOnly` now always
 * renders it `true`: hard-coding it would bake a claim about permission into the
 * one component whose contract is that it makes none.
 */

export interface SpaceListProps {
  /** Drives which controls are offered. Never a substitute for the API's check. */
  isAdmin: boolean;
  /**
   * Whether `CreateSpacePanel` is open. Controlled by `App.tsx` now that the
   * trigger button lives in `SurfaceHeader`'s `actions` slot rather than in
   * this table — see `SurfaceHeader`'s doc for why the panel and its fetch
   * stayed here regardless.
   */
  connectOpen: boolean;
  onCloseConnect: () => void;
  /**
   * The reader's own agency.
   *
   * **It separates leaving from removing** — `DELETE /spaces/{id}/members/{aid}`
   * carries both of CON-14's verbs and the target is what tells them apart
   * (ADR-0071) — and it identifies this agency's own row on a roster, which is
   * what decides whether `MembersPanel` offers Leave or Accept. It is **not**
   * the prop `AgencyGrid` shed with `soleOtherMember`: that one existed to find
   * "the one other agency", a question a roster does not have.
   */
  viewerAgencyId: string;
}

/**
 * Status tones. `Invited` is amber because the design system reserves amber for
 * time and for *active* — an invitation in flight is exactly that. `Removed` is
 * not alert-red: removal is a normal administrative act (CON-14), not a fault,
 * and colouring it as one would misread the room.
 *
 * **The status is this agency's own** (CON-23). A space has no single one.
 */
const STATUS_TONE: Record<SpaceSummary['status'], BadgeTone> = {
  Invited: 'amber',
  Active: 'update',
  Removed: 'neutral',
};

/**
 * How many members the roster cell names before it starts counting.
 *
 * A-10 sizes a space at about twenty-five agencies, and twenty-five names in a
 * table cell is a paragraph in a column. Three is what fits on two lines at the
 * widths this table is read at; the panel is where the whole roster lives.
 */
const ROSTER_CELL_NAMES = 3;

/**
 * The roster summary — the column that used to name "the counterpart".
 *
 * **`Removed` memberships are left out**, because this cell answers "who is in
 * this room" and they are not; they are on the panel, badged, where an admin
 * can see that access ended rather than wondering where an agency went (CON-14).
 * Everything else is named, this agency included: a room is a set of parties and
 * a summary that silently omitted the reader would be describing a different
 * one.
 */
function rosterCell(space: SpaceSummary) {
  const present = space.members.filter((member) => member.status !== 'Removed');
  const named = present.slice(0, ROSTER_CELL_NAMES);
  const hidden = present.length - named.length;

  if (named.length === 0) {
    return (
      <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
        {SPACE_LIST_COPY.roster.none}
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      {named.map((member) => (
        <span
          key={member.agencyId}
          // `bp-text-secondary`, not `bp-text-muted`. The muted rung is 4.1:1 on
          // this background — under AA at this size, and an agency name is
          // exactly the kind of string a reader has to be able to check.
          className="flex items-start gap-2 text-xs"
          style={{ color: token('bp-text-secondary') }}
        >
          {/*
            CON-21's glyph, or nothing — `agencyType` is optional on the wire
            because it predates most fixtures here. `FolderView`'s
            two-line-cell pattern: icon first, `mt-0.5 shrink-0`.
          */}
          {member.agencyType === undefined ? null : (
            <span className="mt-0.5 shrink-0">
              <AgencyTypeIcon
                type={member.agencyType}
                label={SPACE_LIST_COPY.members.agencyTypes[member.agencyType]}
              />
            </span>
          )}
          {member.name}
        </span>
      ))}

      {hidden > 0 ? (
        <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {SPACE_LIST_COPY.roster.more(hidden)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The first column is the **room**, off its own row (CON-22).
 *
 * It was the counterpart's name, its type icon and its domain — three facts
 * about the one other agency there was assumed to be. A room holding a court, a
 * prosecutor and six police agencies has a name somebody chose and a roster, and
 * those are the first two columns now. **The domain left this table's name
 * column and lives on the roster panel**, where a person is deciding who to
 * trust rather than which room to open.
 */
const columns: DataTableColumn<SpaceSummary>[] = [
  {
    id: 'space',
    header: SPACE_LIST_COPY.columns.space,
    sortable: true,
    sortValue: (space: SpaceSummary) => space.name,
    cell: (space: SpaceSummary) => (
      <span className="font-medium" style={{ color: token('bp-text') }}>
        {space.name}
      </span>
    ),
  },
  {
    id: 'members',
    header: SPACE_LIST_COPY.columns.members,
    cell: rosterCell,
  },
  /*
    **There is no Sharing column, and its removal was a product decision.** It
    rendered CON-13's `participation` — "Send + receive" or "Receive only" — as a
    badge on every row, which put a per-membership capability in a table whose
    other five columns are about the room. The fact is not lost: `SpaceRoot`'s
    header states it as the reader's own access badge inside the space it applies
    to, and `SpaceAccessFieldset` reads the same wire field when an admin is
    granting somebody access. This is where it was furniture.
  */
  {
    id: 'status',
    header: SPACE_LIST_COPY.columns.status,
    sortable: true,
    sortValue: (space: SpaceSummary) => space.status,
    cell: (space: SpaceSummary) => (
      <div className="flex flex-col items-start gap-1">
        <Badge tone={STATUS_TONE[space.status]}>{SPACE_LIST_COPY.status[space.status]}</Badge>
        {space.awaitingOurAcceptance ? (
          // The one row in this list that carries an action, so it says so in
          // words rather than relying on the status badge alone (CON-6).
          <span
            className="flex items-center gap-1.5 text-xs"
            style={{ color: token('bp-blue-text') }}
          >
            <StatusDot tone="blue" size="sm" />
            {SPACE_LIST_COPY.awaitingAcceptance}
          </span>
        ) : null}
      </div>
    ),
  },
  {
    id: 'activity',
    header: SPACE_LIST_COPY.columns.activity,
    sortable: true,
    // Undefined sorts **first** ascending — see `compareSortValues`. This
    // comment said "last" for two milestones, which is the reading everybody
    // has of a nullish value and not the one the comparator implements.
    sortValue: (space: SpaceSummary) => space.lastActivityAt,
    cell: (space: SpaceSummary) =>
      space.lastActivityAt === undefined ? (
        <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {SPACE_LIST_COPY.noActivity}
        </span>
      ) : (
        // MetaRow owns the amber-for-time rule, so using it is how this cell
        // gets the treatment right without each caller remembering.
        <MetaRow
          time={formatUtcDate(space.lastActivityAt, SPACE_LIST_COPY.dateLocale)}
          dateTime={space.lastActivityAt}
        />
      ),
  },
  {
    id: 'link',
    header: SPACE_LIST_COPY.columns.link,
    align: 'end',
    /**
     * FIL-9 / CON-15's copy control, moved here from the space root (product
     * direction): an admin sharing a space's URL is managing the connection,
     * which is this table's job now. It replaced "Open space" — browsing into
     * a space is Files' job (ADR-0032), and this table no longer offers a way
     * in, only a way to hand the address to someone else.
     *
     * **The header is "Link", not "Space".** Both this column and the name
     * column were headed "Space" until the roster arrived, which is a table
     * saying one word about two different things.
     *
     * Only a space this agency is an `Active` member of has an address worth
     * handing out: an `Invited` membership is waiting on an acceptance (CON-6)
     * and a `Removed` one has ended (CON-14).
     */
    cell: (space: SpaceSummary) =>
      space.status === 'Active' ? <CopyLinkButton spaceId={space.id} /> : null,
  },
];

/**
 * FIL-9 / CON-15: the **root-only** URL, copied and sent outside the app.
 *
 * Built from `space.id` rather than the current route — this table is not
 * rendered on the space's own page, unlike `SpaceRoot`'s now-removed copy of
 * this control, which read `useLocation()` because it was.
 */
function CopyLinkButton({ spaceId }: { spaceId: string }) {
  const [outcome, setOutcome] = useState<'idle' | 'copied' | 'failed'>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/files/spaces/${encodeURIComponent(spaceId)}`,
      );
      setOutcome('copied');
    } catch {
      // Clipboard access can be refused by the browser, and telling someone to
      // use the address bar is a real answer rather than a dead button.
      setOutcome('failed');
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={() => void copy()}
        className={`inline-block ${BUTTON_CLASS}`}
        style={neutralButtonStyle()}
      >
        {SPACE_LIST_COPY.actions.copyLink}
      </button>

      {outcome === 'idle' ? null : (
        <span
          className="text-xs"
          style={{ color: outcome === 'copied' ? token('bp-text-secondary') : token('bp-danger') }}
        >
          {outcome === 'copied'
            ? SPACE_LIST_COPY.actions.copyLinkDone
            : SPACE_LIST_COPY.actions.copyLinkFailed}
        </span>
      )}
    </div>
  );
}

/**
 * The actions column. Admin-only, and appended rather than woven in, so the
 * table a member sees is exactly the table that shipped before this existed.
 *
 * **Every act behind one trigger now.** Accept (CON-6's, offered only where
 * this agency owes an answer), Members (the way into the roster), and —
 * owner-only — Rename this folder and Delete this space all live inside the
 * "⋯" menu; a row shows nothing else. This used to put Accept and Members on the row itself and
 * hold only Delete behind the overflow, on the reasoning that Members was
 * "this surface's whole point" and didn't belong de-emphasised — that
 * distinction is gone now, in favour of a row that offers exactly one control
 * regardless of which acts apply to it.
 */
function actionsColumn(options: {
  busySpaceId: string | undefined;
  viewerAgencyId: string;
  confirmingDelete: string | undefined;
  onAccept: (space: SpaceSummary) => void;
  onOpenMembers: (space: SpaceSummary) => void;
  onOpenRename: (space: SpaceSummary) => void;
  onOpenWindow: (space: SpaceSummary) => void;
  onAskDelete: (spaceId: string | undefined) => void;
  onDelete: (space: SpaceSummary) => void;
}): DataTableColumn<SpaceSummary> {
  const {
    busySpaceId,
    viewerAgencyId,
    confirmingDelete,
    onAccept,
    onOpenMembers,
    onOpenRename,
    onOpenWindow,
    onAskDelete,
    onDelete,
  } = options;

  return {
    id: 'actions',
    header: SPACE_LIST_COPY.columns.actions,
    align: 'end',
    cell: (space: SpaceSummary) => {
      const busy = busySpaceId === space.id;
      const isOwner = space.members.some(
        (member) => member.agencyId === viewerAgencyId && member.isOwner,
      );

      return (
        <div className="flex items-center justify-end">
          <RowActionsMenu
            label={SPACE_LIST_COPY.actions.moreFor(space.name)}
            glyph={SPACE_LIST_COPY.actions.moreGlyph}
            disabled={busy}
          >
            {confirmingDelete === space.id ? (
              // **CON-26's confirmation, inside the panel it was armed from**
              // (`RowActionsMenu`'s header). Selecting "Delete this space"
              // below keeps the menu open and swaps to this pair rather than
              // closing it, so nobody reopens a menu to see the two words
              // that decide it.
              <RowActionsConfirm
                cancelLabel={SPACE_LIST_COPY.members.deleteSpace.confirm.cancel}
                confirmLabel={
                  busy
                    ? SPACE_LIST_COPY.members.deleteSpace.working
                    : SPACE_LIST_COPY.members.deleteSpace.confirm.submit
                }
                busy={busy}
                onCancel={() => {
                  onAskDelete(undefined);
                }}
                onConfirm={() => {
                  onDelete(space);
                }}
              />
            ) : (
              <>
                {space.awaitingOurAcceptance ? (
                  <RowActionsItem
                    label={
                      busy ? SPACE_LIST_COPY.actions.accepting : SPACE_LIST_COPY.actions.accept
                    }
                    onSelect={() => {
                      onAccept(space);
                    }}
                  />
                ) : null}

                <RowActionsItem
                  label={SPACE_LIST_COPY.actions.members}
                  onSelect={() => {
                    onOpenMembers(space);
                  }}
                />

                {/*
                  Owner-only, read off the roster the row already carries.
                  Visibility, not authorization — the API answers 403 for a
                  non-owner and 409 for a room that still holds files (SEC-5).
                */}
                {/*
                  Owner-only on the same terms as Delete below, and read off
                  the same roster. **No `keepOpen`**: this one opens a drawer,
                  so the menu's default close-on-select is what should happen
                  — a textbox does not belong inside a `role="menu"`, which is
                  why the delete confirmation can live there and this cannot
                  (ADR-0102).
                */}
                {isOwner ? (
                  <RowActionsItem
                    label={SPACE_LIST_COPY.actions.rename}
                    onSelect={() => {
                      onOpenRename(space);
                    }}
                  />
                ) : null}

                {/*
                  Owner-only on the same terms as Rename above (RET-12), and
                  opens a drawer, so no `keepOpen` — the menu's close-on-select
                  is right for a form.
                */}
                {isOwner ? (
                  <RowActionsItem
                    label={SPACE_LIST_COPY.actions.expiryWindow}
                    onSelect={() => {
                      onOpenWindow(space);
                    }}
                  />
                ) : null}

                {isOwner ? (
                  <RowActionsItem
                    label={SPACE_LIST_COPY.members.deleteSpace.label}
                    tone="danger"
                    keepOpen
                    onSelect={() => {
                      onAskDelete(space.id);
                    }}
                  />
                ) : null}
              </>
            )}
          </RowActionsMenu>
        </div>
      );
    },
  };
}

/**
 * Which drawer is open, and over which room.
 *
 * **One at a time, deliberately.** `DetailDrawer` is a modal dialog with a focus
 * trap; two of them mounted together are two traps fighting over Tab. Add an
 * agency is reached *from* the roster, so it replaces it and hands back on close
 * — one drawer on screen, and the admin lands where they started.
 */
type OpenPanel = { kind: 'members' | 'add' | 'rename' | 'window'; spaceId: string } | undefined;

export function SpaceList({
  isAdmin,
  connectOpen,
  onCloseConnect,
  viewerAgencyId,
}: SpaceListProps) {
  const { state, retry, refresh } = useSpaces();
  const [busySpaceId, setBusySpaceId] = useState<string | undefined>();
  const [actionFailed, setActionFailed] = useState(false);
  const [panel, setPanel] = useState<OpenPanel>();
  /**
   * `AddAgencyPanel`'s answer, carried onto the roster it hands over to.
   *
   * Held here rather than in `MembersPanel` because the panel that produces it
   * is not the panel that shows it, and because it must not survive the act it
   * describes — every path that closes the roster or changes it clears this.
   */
  const [inviteNotice, setInviteNotice] = useState<string | undefined>();
  /** CON-26's two stages, on the row rather than in a drawer. */
  const [confirmingDelete, setConfirmingDelete] = useState<string | undefined>();

  /**
   * Run a mutation and refetch.
   *
   * The list is refetched rather than patched: a space's participation, activity
   * date, roster and acceptance cue all move with its status, so the server's
   * answer is the only one worth rendering.
   */
  async function run(spaceId: string, action: (id: string) => Promise<unknown>) {
    setBusySpaceId(spaceId);
    setActionFailed(false);

    try {
      await action(spaceId);
      refresh();
    } catch {
      // Deliberately one message for every failure. A 409 here means the world
      // moved under the admin — someone else accepted, or the membership has
      // already ended — and the honest instruction in every case is to look
      // again.
      setActionFailed(true);
    } finally {
      setBusySpaceId(undefined);
    }
  }

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-secondary') }}>
        {SPACE_LIST_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    // A refusal is permanent — the API decided, and a retry button would
    // promise something it cannot deliver.
    const refused = state.status === 403;

    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {refused ? SPACE_LIST_COPY.states.forbidden : SPACE_LIST_COPY.states.error}
        </p>

        {refused ? null : (
          <button
            type="button"
            onClick={retry}
            className={BUTTON_CLASS}
            style={neutralButtonStyle()}
          >
            {SPACE_LIST_COPY.states.retry}
          </button>
        )}
      </div>
    );
  }

  const shownColumns = isAdmin
    ? [
        ...columns,
        actionsColumn({
          busySpaceId,
          viewerAgencyId,
          confirmingDelete,
          onAccept: (space) => void run(space.id, acceptMembership),
          onOpenMembers: (space) => {
            setActionFailed(false);
            setPanel({ kind: 'members', spaceId: space.id });
          },
          onOpenRename: (space) => {
            setActionFailed(false);
            setPanel({ kind: 'rename', spaceId: space.id });
          },
          onOpenWindow: (space) => {
            setActionFailed(false);
            setPanel({ kind: 'window', spaceId: space.id });
          },
          onAskDelete: setConfirmingDelete,
          onDelete: (space) => {
            setConfirmingDelete(undefined);
            // `run` refetches, which is what removes the row — the list is the
            // only thing that knows what is true after a delete.
            void run(space.id, deleteSpace);
          },
        }),
      ]
    : columns;

  // Re-read from the freshly fetched list rather than held in state, so a
  // refetch after Add or Remove renders the new roster in the open drawer
  // instead of the one that was captured when it opened.
  const openSpace =
    panel === undefined ? undefined : state.spaces.find((space) => space.id === panel.spaceId);

  return (
    <div className="flex flex-col gap-4">
      {actionFailed ? (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {SPACE_LIST_COPY.actions.failed}
        </p>
      ) : null}

      <DataTable
        columns={shownColumns}
        rows={state.spaces}
        rowKey={(space) => space.id}
        caption={SPACE_LIST_COPY.caption}
        emptyState={SPACE_LIST_COPY.states.empty}
        // The same resting order every other table in this app opens on: its
        // name column, ascending. `useSpaces` makes one unpaged request, so the
        // client holds every row it is ordering.
        defaultSort={{ id: 'space', direction: 'asc' }}
      />

      {isAdmin ? (
        <CreateSpacePanel
          open={connectOpen}
          onClose={onCloseConnect}
          /*
            **Creating hands over to the new room's roster.** The panel used to
            stay open reporting `"…" is open. Add an agency to it from its
            members panel` — an instruction to go and do by hand the thing this
            now does, leaving an admin on a spent form with the roster two
            clicks away.

            The drawer that opens is derived from the refetched list, so there
            is a frame or two with neither panel on screen while `refresh()` is
            in flight. That is the correct shape rather than a gap to paper
            over: `createSpace` answers with a `SpaceView`, and `MembersPanel`
            reads a `SpaceSummary` — seeding it from the response would be a
            second, thinner source of truth for a roster this file otherwise
            only ever refetches.
          */
          onCreated={(spaceId) => {
            refresh();
            onCloseConnect();
            setActionFailed(false);
            setPanel({ kind: 'members', spaceId });
          }}
        />
      ) : null}

      {isAdmin && openSpace !== undefined && panel?.kind === 'members' ? (
        <MembersPanel
          open
          onClose={() => {
            setPanel(undefined);
            setInviteNotice(undefined);
          }}
          space={openSpace}
          viewerAgencyId={viewerAgencyId}
          notice={inviteNotice}
          onAddAgency={() => {
            setInviteNotice(undefined);
            setPanel({ kind: 'add', spaceId: openSpace.id });
          }}
          onChanged={() => {
            // A removal or a scope change makes the invitation line stale — it
            // describes an act two acts ago.
            setInviteNotice(undefined);
            refresh();
          }}
          onDeleted={() => {
            // The room is gone: close the drawer rather than refetching a
            // roster for a space that no longer exists.
            setInviteNotice(undefined);
            setPanel(undefined);
            refresh();
          }}
        />
      ) : null}

      {isAdmin && openSpace !== undefined && panel?.kind === 'add' ? (
        <AddAgencyPanel
          open
          // Back to the roster it was reached from, rather than to the table —
          // an admin who has just invited somebody is looking at a list they
          // expect to have changed.
          onClose={() => {
            setPanel({ kind: 'members', spaceId: openSpace.id });
          }}
          spaceId={openSpace.id}
          spaceName={openSpace.name}
          /*
            **The answer travels with the reader.** This panel stayed open on
            success because `existing` and `invited` read identically on a
            roster — one agency joins when an admin there accepts, the other
            when somebody there signs in for the first time — and only the API
            can tell them apart. Landing on the roster without it would leave an
            admin looking at a row and unable to learn which of the two they are
            waiting on. So the panel hands over and the sentence goes with it.
          */
          onAdded={() => {
            refresh();
            setInviteNotice(SPACE_LIST_COPY.addAgency.result);
            setPanel({ kind: 'members', spaceId: openSpace.id });
          }}
        />
      ) : null}

      {isAdmin && openSpace !== undefined && panel?.kind === 'rename' ? (
        <RenameSpacePanel
          open
          onClose={() => {
            setPanel(undefined);
          }}
          spaceId={openSpace.id}
          spaceName={openSpace.name}
          /*
            **Closes rather than handing over**, which is the opposite of
            `CreateSpacePanel` one block up. Creating hands over to the new
            room's roster because an empty room is not a finished job; a
            renamed room is, and the refetch puts the new name on the row the
            admin opened the menu from.
          */
          onRenamed={() => {
            setPanel(undefined);
            setActionFailed(false);
            refresh();
          }}
        />
      ) : null}

      {isAdmin && openSpace !== undefined && panel?.kind === 'window' ? (
        <ExpiryWindowPanel
          open
          onClose={() => {
            setPanel(undefined);
          }}
          spaceId={openSpace.id}
          spaceName={openSpace.name}
          currentWindowDays={openSpace.expiryWindowDays ?? ACTIVE_WINDOW_DEFAULT_DAYS}
          /*
            **Refetches but does not close** — the result, including how many
            files the shorten brings due, stays on screen until the owner
            dismisses it. The refresh puts the new keep-for value on the row.
          */
          onChanged={() => {
            setActionFailed(false);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}
